import React, { useEffect, useState } from "react";
import fallbackimage from "components/assets/User.png";
import SkeletonLoader from "components/Loaders/SkeletonLoader";
import { fetchUserProfileAPI } from "components/API/ApiEnpoints";
import {
  FaUser,
  FaUserCircle,
  FaEnvelope,
  FaBuilding,
  FaCity,
  FaUsers,
  FaPhoneAlt,
  FaGlobe,
} from "react-icons/fa";
import openNotification from "components/utils/CustomNotification";
import "./TeamStats.css";
import {
  MdOutlineDeviceUnknown,
  MdOutlineNotificationsActive,
} from "react-icons/md";

interface Member {
  id: string;
  name: string;
  avatar: string;
  points: number;
  isOnline?: boolean;
  user_uid?: string;
  unit: string;
}

interface TeamData {
  id: string;
  name: string;
  percentage: number;
  members: Member[];
  color: string;
}
interface LeaderboardUser {
  user_uid: string;
  user_name: string;
  image: string; // base64 image
  value: number;
}
interface DepartmentData {
  id: string;
  name: string;
  percentage: number;
  color: string;
}

const TeamStats: React.FC = () => {
  const [metricsData, setMetricsData] = useState<any>(null);
  const [topMembers, setTopMembers] = useState<Member[]>([]);
  const [topProjects, setTopProjects] = useState<any[]>([]);
  const [topDepartments, setTopDepartments] = useState<any[]>([]);
  const [topLocations, setTopLocations] = useState<any[]>([]);
  const [showAllDepartments, setShowAllDepartments] = useState(false);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [selectedMetricProjects, setSelectedMetricProjects] = useState<
    "performance" | "steps" | "distance" | "calories"
  >("performance");
  const [selectedMetricDepartments, setSelectedMetricDepartments] = useState<
    "performance" | "steps" | "distance" | "calories"
  >("performance");
  const [selectedMetricLocations, setSelectedMetricLocations] = useState<
    "performance" | "steps" | "distance" | "calories"
  >("performance");

  const [dataLoading, setDataLoading] = useState(true);
  const [leaderboarddataLoading, setLeaderboarddataLoading] = useState(true);
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const orgUid = localStorage.getItem("org_uid") || "Organization";
  const loggedInUserUid = localStorage.getItem("user_uid");

  const [showAll, setShowAll] = useState(false);
  const visibleMembers = showAll ? topMembers : topMembers.slice(0, 5);
  const toggleShowAll = () => setShowAll((prev) => !prev);
  // Sample data - replace with your actual data
  const beAddress = process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const formatDateTimeForAPI = (date: Date, timeZone: string) => {
    // Convert to target timezone
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const d: Record<string, string> = {};
    parts.forEach((p) => {
      if (p.type !== "literal") d[p.type] = p.value;
    });

    // Format: YYYY-MM-DDTHH:mm:ss (local time, no Z)
    return `${d.year}-${d.month}-${d.day}T${d.hour}:${d.minute}:${d.second}`;
  };

  const handleUserClick = (member: any) => {
    //  Show modal immediately with leaderboard info
    setUserProfile({
      image: member.avatar, // reuse leaderboard avatar
      first_name: member.name?.split(" ")[0] || "",
      last_name: member.name?.split(" ")[1] || "",
      // fallback basic info until API returns
    });
    setIsProfilePopupOpen(true);

    //  Then fetch more details in background
    const beAddress = process.env.REACT_APP_API_URL!;
    const sessionUid = localStorage.getItem("session_uid") || "";
    const userUid = localStorage.getItem("user_uid") || "";

    if (!member.user_uid) return; // skip if UID missing

    fetchUserProfileAPI(beAddress, sessionUid, userUid, member.user_uid).then(
      (profile) => {
        if (profile) {
          setUserProfile((prev: any) => ({
            ...prev,
            ...profile,
            //  Keep the original avatar if API has no image
            image: prev.image || profile.image || null,
          }));
        }
      }
    );
  };

  const fetchImagesByUid = async (
    imageUids: string[]
  ): Promise<Record<string, string>> => {
    if (!imageUids || imageUids.length === 0) {
      return {};
    }

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);
      imageUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid)
      );
      url.searchParams.set("is_icon_img", "true");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": userUid,
        }, credentials: "include",
      });

      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => {
            if (item.default) return [item.image_uid, ""];
            return [item.image_uid, item.image];
          })
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }

    return {};
  };

  const fetchOrgLeaderboard = async () => {
    try {
      setLeaderboarddataLoading(true);

      const now = new Date();
      const utcYear = now.getUTCFullYear();
      const utcMonth = now.getUTCMonth();
      const utcDate = now.getUTCDate();

      const startOfTodayUTC = new Date(
        Date.UTC(utcYear, utcMonth, utcDate, 0, 0, 0, 0)
      );
      const endOfTodayUTC = new Date(
        Date.UTC(utcYear, utcMonth, utcDate, 23, 59, 59, 999)
      );

      const fromDatetime = startOfTodayUTC.toISOString().split(".")[0] + "Z";
      const toDatetime = endOfTodayUTC.toISOString().split(".")[0] + "Z";

      const url = new URL(
        `${process.env.REACT_APP_API_URL}/api/get-team-leaderboard`
      );
      url.searchParams.append("from_datetime", fromDatetime);
      url.searchParams.append("to_datetime", toDatetime);
      url.searchParams.append("is_org_leaderboard", "true");
      url.searchParams.append("filter_type", "steps");

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": localStorage.getItem("user_uid") || "",
        }, credentials: "include",
      });

      if (res.status === 401) {
        openNotification({
          type: "error",
          message: "Session expired. Please log in again.",
        });
        return;
      }

      const data = await res.json();

      if (data?.status !== "Success") {
        openNotification({
          type: "error",
          message: data?.message || "Failed to load leaderboard data",
        });
        return;
      }

      if (!Array.isArray(data.data)) {
        openNotification({
          type: "warning",
          message: "No leaderboard data available today.",
        });
        return;
      }

      const top10 = data.data.slice(0, 10);

      const imageUids = top10
        .filter((m: any) => m.image_uid && m.image_uid !== "")
        .map((m: any) => m.image_uid);

      const imageResponse = await fetchImagesByUid(imageUids);

      const mapped = top10.map((member: any) => {
        const fetchedBase64 = imageResponse[member.image_uid];
        const validImage = !!fetchedBase64;

        let imageSrc = "";

        if (validImage) {
          let mimeType = "image/png";
          if (fetchedBase64.startsWith("R0lGODlh")) mimeType = "image/gif";
          else if (fetchedBase64.startsWith("/9j/")) mimeType = "image/jpeg";
          else if (fetchedBase64.startsWith("iVBORw0KGgo")) mimeType = "image/png";

          imageSrc = `data:${mimeType};base64,${fetchedBase64}`;
        }

        return {
          id: member.user_uid,
          user_uid: member.user_uid,
          name: member.user_name,
          points: member.value,
          avatar: imageSrc,
          hasValidAvatar: validImage,
          unit: member.unit,
        };
      });

      setTopMembers(mapped);

      if (mapped.length === 0) {
        openNotification({
          type: "info",
          message: "No top performers available today.",
        });
      }
    } catch (error: any) {
      openNotification({
        type: "error",
        message: error?.message || "Something went wrong fetching leaderboard",
      });
    } finally {
      setLeaderboarddataLoading(false);
    }
  };


  const fetchTopMetrics = async (
    teamType: "project" | "department" | "location"
  ) => {
    try {
      setDataLoading(true);

      const now = new Date();
      const utcYear = now.getUTCFullYear();
      const utcMonth = now.getUTCMonth();
      const utcDate = now.getUTCDate();

      const startOfTodayUTC = new Date(
        Date.UTC(utcYear, utcMonth, utcDate, 0, 0, 0, 0)
      );
      const endOfTodayUTC = new Date(
        Date.UTC(utcYear, utcMonth, utcDate, 23, 59, 59, 999)
      );

      const fromDatetime = startOfTodayUTC.toISOString().split(".")[0] + "Z";
      const toDatetime = endOfTodayUTC.toISOString().split(".")[0] + "Z";

      const url = new URL(
        `${process.env.REACT_APP_API_URL}/api/team-performance-metrics`
      );
      url.searchParams.append("from_datetime", fromDatetime);
      url.searchParams.append("to_datetime", toDatetime);
      url.searchParams.append("team_type", teamType);

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": localStorage.getItem("user_uid") || "",
        }, credentials: "include",
      });

      if (res.status === 401) {
        openNotification({
          type: "error",
          message: "Session expired. Please log in again.",
        });
        return;
      }

      const data = await res.json();

      if (data?.status !== "Success") {
        openNotification({
          type: "error",
          message: data?.message || `Failed to load ${teamType} metrics`,
        });
        return;
      }

      if (!Array.isArray(data.data)) {
        openNotification({
          type: "warning",
          message: `No ${teamType} data available today.`,
        });
        return;
      }

      const mapped = data.data.slice(0, 10).map((item: any, index: number) => {
        const metrics = item.metric_summary?.metrics?.[0] || {};

        return {
          id: `${teamType}_${index}`,
          name:
            teamType === "department"
              ? item.department || "Unknown Dept"
              : teamType === "location"
                ? item.location || "Unknown Location"
                : item.project || "Unknown Project",

          performance: item.metric_summary?.overall_performance
            ? parseFloat(item.metric_summary.overall_performance.toFixed(1))
            : 0,

          percentage: item.metric_summary?.overall_performance
            ? parseFloat(item.metric_summary.overall_performance.toFixed(1))
            : 0,

          active: item.active_users || 0,
          total: item.users_count || 0,
          teamScore: item.team_score || 0,

          // steps: metrics.steps?.value || 0,
          // distance: metrics.distance?.value || 0,
          // calories: metrics.caloriesburned?.value || 0,
          steps: {
            value: metrics.steps?.value || 0,
            unit: metrics.steps?.unit || "steps",
          },
          distance: {
            value: metrics.distance?.value || 0,
            unit: metrics.distance?.unit || "km",
          },
          calories: {
            value: metrics.caloriesburned?.value || 0,
            unit: metrics.caloriesburned?.unit || "kcal",
          },

          color:
            teamType === "department"
              ? "#4ECDC4"
              : teamType === "location"
                ? "#45B7D1"
                : "#FFA07A",
        };
      });

      if (teamType === "department") setTopDepartments(mapped);
      else if (teamType === "location") setTopLocations(mapped);
      else setTopProjects(mapped);

      if (mapped.length === 0) {
        openNotification({
          type: "info",
          message: `No top ${teamType}s found for today.`,
        });
      }
    } catch (error: any) {
      openNotification({
        type: "error",
        message:
          error?.message || `Something went wrong fetching ${teamType} metrics`,
      });
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgLeaderboard();
    fetchTopMetrics("department");
    fetchTopMetrics("location");
    fetchTopMetrics("project");
  }, []);

  const renderEnhancedTable = (
    data: any[],
    teamType: "department" | "location" | "project",
    showAll: boolean,
    setShowAll: (value: boolean) => void,
    selectedMetric: "performance" | "steps" | "distance" | "calories",
    setSelectedMetric: (
      value: "performance" | "steps" | "distance" | "calories"
    ) => void
  ) => {
    const displayData = showAll ? data : data.slice(0, 2);
    const teamLabel = teamType.charAt(0).toUpperCase() + teamType.slice(1);

    return (
      <div className="teams-stats__teams-table-container">
        <table className="teams-stats__teams-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>{teamLabel}</th>
              <th>
                <select
                  className="teams-stats__metric-dropdown"
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value as any)}
                >
                  <option value="performance">Performance</option>
                  <option value="steps">Steps</option>
                  <option value="distance">Distance</option>
                  <option value="calories">Calories</option>
                </select>
              </th>
              <th>Score</th>
              <th>Users</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((team: any, index: number) => {
              const actualRank = data.findIndex((t) => t.id === team.id) + 1;
              const registeredCount = team.active || 0;
              const totalCount = team.total || 0;

              return (
                <tr key={team.id} className="teams-stats__team-table-row">
                  <td className="teams-stats__team-table-cell">
                    <div className="teams-stats__table-rank">{actualRank}</div>
                  </td>
                  <td className="teams-stats__team-table-cell">
                    <div className="teams-stats__table-team-name">
                      {team.name}
                    </div>
                  </td>
                  <td className="teams-stats__team-table-cell">
                    {selectedMetric === "performance" && (
                      <div className="teams-stats__table-performance">
                        {team.performance.toFixed(2)}%
                      </div>
                    )}
                    {selectedMetric === "steps" && (
                      <div className="teams-stats__table-performance">
                        {team.steps.value} {team.steps.unit}
                      </div>
                    )}
                    {selectedMetric === "distance" && (
                      <div className="teams-stats__table-performance">
                        {team.distance.value} {team.distance.unit}
                      </div>
                    )}
                    {selectedMetric === "calories" && (
                      <div className="teams-stats__table-performance">
                        {team.calories.value} {team.calories.unit}
                      </div>
                    )}
                  </td>
                  <td className="teams-stats__team-table-cell">
                    <div className="teams-stats__table-score">
                      {team.teamScore}
                    </div>
                  </td>
                  <td className="teams-stats__team-table-cell">
                    <div className="teams-stats__table-users">
                      {registeredCount}/{totalCount}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {data.length > 5 && (
          <div className="teams-stats__view-more-container">
            <button
              className="teams-stats__view-more-enhanced"
              onClick={() => setShowAll(!showAll)}
            >
              <span className="teams-stats__view-more-text">
                {showAll ? `Show Less` : `View More`}
              </span>
              <div className="teams-stats__view-more-icon">
                {showAll ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                )}
              </div>
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPodiumMember = (member?: any, rank?: number) => {
    if (!member) return null;

    return (
      <div
        className={`teams-stats__podium-member teams-stats__podium-member--${rank}`}
        key={member.id}
      >
        <div style={{ position: "relative" }}>
          {member.hasValidAvatar ? (
            <div className="teams-stats__podium-avatar">
              <img
                src={member.avatar}
                alt={member.name || "User"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          ) : (
            <div className="teams-stats__podium-avatar-container">
              <FaUserCircle className="podium-fallback-icon" />
            </div>
          )}

          {/* Circular badge below avatar */}
          <div className="teams-stats__podium-badge">{rank}</div>
        </div>

        <div className="teams-stats__podium-name">
          {member.name || "Unknown"}
        </div>
        <div className="teams-stats__podium-points">
          {member.points || 0} {member.unit || "steps"}
        </div>
      </div>
    );
  };
  // Updated renderListMember function
  const renderListMember = (member?: any, rank?: number) => {
    if (!member) return null;

    return (
      <div className="teams-stats__list-member" key={member.id}>
        <span className="teams-stats__list-rank">{rank}</span>

        {member.hasValidAvatar ? (
          <img
            className="teams-stats__list-avatar"
            src={member.avatar}
            alt={member.name || "User"}
          />
        ) : (
          <FaUserCircle className="teams-stats__list-avatar list-fallback-icon" />
        )}

        <div className="teams-stats__list-info">
          <span className="teams-stats__list-name">
            {member.name || "Unknown"}
          </span>
          <span className="teams-stats__list-points">
            {member.points || 0} {member.unit || "steps"}
          </span>
        </div>
      </div>
    );
  };

  const renderTeamMetricCard = (team: any) => (
    <div key={team.id} className="teams-stats__team-card">
      <div className="teams-stats__team-header">
        <h4>{team.name}</h4>
        {/* <span className="teams-stats__team-percentage">{team.percentage}%</span> */}
      </div>

      {/*  Registered users */}
      <div className="teams-stats__team-info">
        <span>
          Registered: {team.active} / {team.total}
        </span>
      </div>

      {/* ✅ Score */}
      <div className="teams-stats__team-info">
        <span>Score: {team.teamScore}</span>
      </div>
    </div>
  );

  const renderTeamCard = (team: TeamData, index: number) => (
    <div key={team.id} className="teams-stats__team-card">
      <div className="teams-stats__team-header">
        <h4>{team.name}</h4>
        <span className="teams-stats__team-percentage">{team.percentage}%</span>
      </div>
      <div className="teams-stats__team-members">
        {team.members.map((member) => (
          <div key={member.id} className="teams-stats__team-member">
            <img src={member.avatar} alt={member.name} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderBarChart = (data: DepartmentData[]) => {
    //  Sort teams by highest performance first
    const sortedData = [...data].sort((a, b) => b.percentage - a.percentage);

    //  Take only Top 5 Teams
    const topFiveTeams = sortedData.slice(0, 5);

    //  Find max value for scaling bar heights
    const maxValue = Math.max(...topFiveTeams.map((item) => item.percentage));

    return (
      <div className="teams-stats__bar-chart">
        {topFiveTeams.map((item, index) => {
          const relativeHeight = (item.percentage / maxValue) * 100;

          return (
            <div key={item.id} className="teams-stats__bar-item">
              {/*  Percentage above */}
              <div className="teams-stats__bar-value">
                {item.percentage.toFixed(1)}%
              </div>

              {/* Fixed-height bar container */}
              <div className="teams-stats__bar-container">
                <div
                  className="teams-stats__bar"
                  style={{
                    height: `${relativeHeight}%`,
                    backgroundColor: item.color,
                  }}
                />
              </div>

              {/*  Team name below the bar */}
              <div className="teams-stats__bar-label-slot">
                <div className="teams-stats__bar-name-wrap" title={item.name}>
                  {item.name}
                </div>
              </div>

              {/*  Rank badge below team name */}
              <div className="teams-stats__bar-rank">{index + 1}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSectionCard = (title: string, data: DepartmentData[]) => (
    <>
      <div className="teams-stats__section-header">
        <h3>{title}</h3>
        <button className="teams-stats__view-more">View more →</button>
      </div>
      <div className="teams-stats__section-content">
        {renderBarChart(data)}
        <div className="teams-stats__section-details">
          {data.map((item, index) => (
            <div key={item.id} className="teams-stats__detail-item">
              <div className="teams-stats__detail-rank">{index + 1}</div>
              <div className="teams-stats__detail-info">
                <span className="teams-stats__detail-name">{item.name}</span>
                <span className="teams-stats__detail-percentage">
                  {item.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "active":
        return { color: "#16821aff", label: "Online", iconname: "checkcircle" }; // green 16821aff
      case "away":
        return { color: "#f6c70aff", label: "Away", iconname: "clockcircle" }; // yellow f6c70aff
      case "invited":
        return {
          color: "#000000",
          label: "Inactive",
          iconname: "exclamationcircle",
        }; // black
      default:
        return {
          color: "#9b9292ff",
          label: "Offline",
          iconname: "closecircle",
        }; // black
    }
  };

  return (
    <div className="teams-stats">
      {/* Top Row: Members and Teams */}
      <div className="teams-stats__layout">
        {/* LEFT COLUMN */}
        <div className="teams-stats__col">
          {/* Top Members Section */}
          <div className="teams-stats__top-members">
            <div className="teams-stats__section-header">
              <h3>{(orgUid || "").toUpperCase()} Top Performers</h3>
            </div>

            {/*  Show skeleton loader while loading */}
            {leaderboarddataLoading ? (
              <SkeletonLoader type="leaderboard" />
            ) : topMembers.length === 0 ? (
              //  Empty state if no data returned
              <div className="teams-stats__empty-state">
                <img src="/no-data.svg" alt="No performers" />
                <p>No top performers available yet</p>
              </div>
            ) : (
              <>
                {/*  Podium: Always show 3 slots */}
                <div className="teams-stats__podium">
                  {[1, 0, 2].map((rankIndex) => {
                    const member = topMembers[rankIndex];

                    if (member) {
                      //  Real podium member (clickable)
                      return (
                        <div
                          key={rankIndex}
                          className="teams-stats__podium-clickable"
                          onClick={() => handleUserClick(member)}
                          style={{ cursor: "pointer" }}
                        >
                          {renderPodiumMember(member, rankIndex + 1)}
                        </div>
                      );
                    }

                    //  Fallback if no user for this slot (NOT clickable)
                    return (
                      <div
                        key={rankIndex}
                        className="teams-stats__podium-member fallback"
                      >
                        <div className="teams-stats__podium-avatar-container">
                          <FaUser className="podium-fallback-icon" />
                          {/*  Same badge styling as real members */}
                          <div className="teams-stats__podium-badge">
                            {rankIndex + 1}
                          </div>
                        </div>
                        <div className="podium-fallback-name">
                          Not yet claimed
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/*  List: Always exactly 2 slots for ranks 4 & 5 */}
                <div className="teams-stats__members-list">
                  {(() => {
                    const loggedInUser = topMembers.find(
                      (m) => m.user_uid === loggedInUserUid
                    );
                    const loggedInUserIndex = topMembers.findIndex(
                      (m) => m.user_uid === loggedInUserUid
                    );
                    const isUserAboveTop5 =
                      loggedInUser && loggedInUserIndex >= 5;

                    if (showAll) {
                      let membersToShow = [...topMembers.slice(3)];

                      const alreadyInList = membersToShow.some(
                        (m) => m.user_uid === loggedInUserUid
                      );
                      if (isUserAboveTop5 && !alreadyInList && loggedInUser) {
                        membersToShow.push(loggedInUser);
                      }

                      return membersToShow.map((member, index) => (
                        <div
                          key={member.user_uid || member.id || index}
                          className={`teams-stats__list-clickable ${member.user_uid === loggedInUserUid
                              ? "highlight-self"
                              : ""
                            }`}
                          onClick={() => handleUserClick(member)}
                          style={{ cursor: "pointer" }}
                        >
                          {renderListMember(
                            {
                              ...member,
                              name:
                                member.user_uid === loggedInUserUid
                                  ? "You"
                                  : member.name,
                            },
                            topMembers.findIndex(
                              (m) => m.user_uid === member.user_uid
                            ) + 1
                          )}
                        </div>
                      ));
                    } else {
                      const rows = [];

                      if (isUserAboveTop5 && loggedInUser) {
                        // Show "You" (real rank), then 4th and 5th place
                        rows.push(
                          <div
                            key={loggedInUser.user_uid}
                            className="teams-stats__list-clickable highlight-self"
                            onClick={() => handleUserClick(loggedInUser)}
                            style={{ cursor: "pointer" }}
                          >
                            {renderListMember(
                              {
                                ...loggedInUser,
                                name: "You",
                              },
                              loggedInUserIndex + 1
                            )}
                          </div>
                        );

                        // Actual 4th and 5th members (index 3 & 4)
                        [3, 4].forEach((rankIndex) => {
                          const member = topMembers[rankIndex];
                          if (member) {
                            rows.push(
                              <div
                                key={member.user_uid || member.id || rankIndex}
                                className={`teams-stats__list-clickable ${member.user_uid === loggedInUserUid
                                    ? "highlight-self"
                                    : ""
                                  }`}
                                onClick={() => handleUserClick(member)}
                                style={{ cursor: "pointer" }}
                              >
                                {renderListMember(
                                  {
                                    ...member,
                                    name:
                                      member.user_uid === loggedInUserUid
                                        ? "You"
                                        : member.name,
                                  },
                                  rankIndex + 1
                                )}
                              </div>
                            );
                          } else {
                            // Fallback if user missing at that rank
                            rows.push(
                              <div
                                key={`fallback-${rankIndex}`}
                                className="teams-stats__list-member fallback"
                              >
                                <span className="teams-stats__list-rank">
                                  {rankIndex + 1}
                                </span>
                                <FaUserCircle className="teams-stats__list-avatar list-fallback-icon" />
                                <div className="teams-stats__list-info">
                                  <span className="teams-stats__list-name">
                                    Not yet claimed
                                  </span>
                                  <span className="teams-stats__list-points">
                                    --%
                                  </span>
                                </div>
                              </div>
                            );
                          }
                        });

                        return rows;
                      }

                      // Default: just 4th and 5th users
                      return [3, 4].map((rankIndex) => {
                        const member = topMembers[rankIndex];

                        if (member) {
                          return (
                            <div
                              key={member.user_uid || member.id || rankIndex}
                              className={`teams-stats__list-clickable ${member.user_uid === loggedInUserUid
                                  ? "highlight-self"
                                  : ""
                                }`}
                              onClick={() => handleUserClick(member)}
                              style={{ cursor: "pointer" }}
                            >
                              {renderListMember(
                                {
                                  ...member,
                                  name:
                                    member.user_uid === loggedInUserUid
                                      ? "You"
                                      : member.name,
                                },
                                rankIndex + 1
                              )}
                            </div>
                          );
                        }

                        return (
                          <div
                            key={`fallback-${rankIndex}`}
                            className="teams-stats__list-member fallback"
                          >
                            <span className="teams-stats__list-rank">
                              {rankIndex + 1}
                            </span>
                            <FaUserCircle className="teams-stats__list-avatar list-fallback-icon" />
                            <div className="teams-stats__list-info">
                              <span className="teams-stats__list-name">
                                Not yet claimed
                              </span>
                              <span className="teams-stats__list-points">
                                --%
                              </span>
                            </div>
                          </div>
                        );
                      });
                    }
                  })()}
                </div>

                {/*  View More/Less Button - Only if more than 5 members */}
                {topMembers.length > 5 && (
                  <div className="teams-stats__view-more-container">
                    <button
                      className="teams-stats__view-more-enhanced"
                      onClick={toggleShowAll}
                    >
                      <span className="teams-stats__view-more-text">
                        {showAll ? `Show Less` : `View More`}
                      </span>
                      <div className="teams-stats__view-more-icon">
                        {showAll ? (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="m18 15-6-6-6 6" />
                          </svg>
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        )}
                      </div>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Left Bottom Section */}
          <div className="teams-stats__top-teams">
            <div className="teams-stats__section-header">
              <h3>Top Locations</h3>
              {/* <div className="teams-stats__info-icon">i</div> */}
            </div>

            {dataLoading ? (
              <SkeletonLoader type="vertical-chart" />
            ) : topLocations.length === 0 ? (
              //  Empty state when API returns no data
              <div className="teams-stats__empty-state">
                <img src="/no-data.svg" alt="No data" />
                <p>No location performance data available</p>
              </div>
            ) : (
              <>
                <div className="teams-stats__top-teams-content">
                  {/*  Show top 5 in chart */}
                  <div className="teams-stats__teams-chart">
                    {renderBarChart(topLocations.slice(0, 5))}
                  </div>

                  {/*  Show top 3 as cards */}
                  <div className="teams-stats__teams-cards">
                    {topLocations
                      .slice(0, 3)
                      .map((team: any) => renderTeamMetricCard(team))}
                  </div>
                </div>

                {/*  Enhanced Table with View More */}
                {renderEnhancedTable(
                  topLocations,
                  "location",
                  showAllLocations,
                  setShowAllLocations,
                  selectedMetricLocations,
                  setSelectedMetricLocations
                )}
              </>
            )}
          </div>
        </div>

        <div className="teams-stats__col">
          <div className="teams-stats__top-teams">
            <div className="teams-stats__section-header">
              <h3>Top Departments</h3>
              {/* <div className="teams-stats__info-icon">i</div> */}
            </div>

            {/*  Show skeleton while loading */}
            {dataLoading ? (
              <SkeletonLoader type="vertical-chart" />
            ) : topDepartments.length === 0 ? (
              // Empty state if no data after loading
              <div className="teams-stats__empty-state">
                <img src="/no-data.svg" alt="No data" />
                <p>No department performance data available</p>
              </div>
            ) : (
              <>
                <div className="teams-stats__top-teams-content">
                  {/*  Show top 5 in bar chart */}
                  <div className="teams-stats__teams-chart">
                    {renderBarChart(topDepartments.slice(0, 5))}
                  </div>

                  {/* Show top 3 as cards */}
                  <div className="teams-stats__teams-cards">
                    {topDepartments
                      .slice(0, 3)
                      .map((team: any) => renderTeamMetricCard(team))}
                  </div>
                </div>

                {/*  Enhanced Table with View More */}
                {renderEnhancedTable(
                  topDepartments,
                  "department",
                  showAllDepartments,
                  setShowAllDepartments,
                  selectedMetricDepartments,
                  setSelectedMetricDepartments
                )}
              </>
            )}
          </div>

          <div className="teams-stats__top-teams">
            <div className="teams-stats__section-header">
              <h3>Top Projects</h3>
              {/* <div className="teams-stats__info-icon">i</div> */}
            </div>

            {/*  Show skeleton while loading */}
            {dataLoading ? (
              <SkeletonLoader type="vertical-chart" />
            ) : topProjects.length === 0 ? (
              //  Empty state when no data after loading
              <div className="teams-stats__empty-state">
                <img src="/no-data.svg" alt="No data" />
                <p>No project performance data available</p>
              </div>
            ) : (
              <>
                <div className="teams-stats__top-teams-content">
                  {/*  Show top 5 in bar chart */}
                  <div className="teams-stats__teams-chart">
                    {renderBarChart(topProjects.slice(0, 5))}
                  </div>

                  {/*  Show top 3 as cards */}
                  <div className="teams-stats__teams-cards">
                    {topProjects
                      .slice(0, 3)
                      .map((team: any) => renderTeamMetricCard(team))}
                  </div>
                </div>

                {/*  Enhanced Table with View More */}
                {renderEnhancedTable(
                  topProjects,
                  "project",
                  showAllProjects,
                  setShowAllProjects,
                  selectedMetricProjects,
                  setSelectedMetricProjects
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* <div className="teams-stats__bottom-row">
        {renderSectionCard('Top Departments', topDepartments)}
        {renderSectionCard('Top Locations', topLocations)}
        {renderSectionCard('Top Projects', topProjects)}
      </div> */}
      {isProfilePopupOpen && userProfile && (
        <div className="member-profile-popup">
          <div className="member-profile-popup-content">
            <span
              className="member-profile-close"
              onClick={() => setIsProfilePopupOpen(false)}
            >
              &times;
            </span>

            <div className="member-profile-header">
              {userProfile.image ? (
                <img
                  src={userProfile.image}
                  alt={`${userProfile.first_name} ${userProfile.last_name}`}
                  className="member-profile-image"
                  style={{
                    border: `3px solid ${getStatusInfo(userProfile.user_online_status).color
                      }`,
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <FaUserCircle
                  className="member-profile-placeholder"
                  style={{
                    color: getStatusInfo(userProfile.user_online_status).color,
                    border: `3px solid ${getStatusInfo(userProfile.user_online_status).color
                      }`,
                    borderRadius: "50%",
                    padding: "3px",
                  }}
                />
              )}
              <div
                style={{
                  textAlign: "center",
                  marginTop: "2px",
                  fontWeight: "500",
                  color: getStatusInfo(userProfile.user_online_status).color,
                }}
              >
                {getStatusInfo(userProfile.user_online_status).label}
              </div>
              <div className="member-name-job">
                <h2>{`${userProfile.first_name} ${userProfile.last_name}`}</h2>
                <p className="member-job-title">{userProfile.job_title}</p>
              </div>
            </div>

            <div className="leaderboard-member-details">
              <div className="leaderboard-popup-row">
                <FaEnvelope className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Email:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile.email}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaBuilding className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Department:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile.department}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaPhoneAlt className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Mobile:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile.mobile_phone || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaGlobe className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Nationality:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile.nationality || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaCity className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">City:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile.city || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaUsers className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Team:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile.team || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <MdOutlineDeviceUnknown className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Device type:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile.device_type || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <MdOutlineNotificationsActive className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Last Active:</div>
                  <div className="leaderboard-popup-value">
                    {userProfile?.last_active
                      ? new Date(userProfile.last_active).toLocaleString()
                      : "Never logged in"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamStats;
